// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useContactUsBranches, warehouseOptionLabel } from "@conklin/App/Widgets/ContactUs/contactUsBranches";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    defaultBranch: state.components.contactUsForm.fieldValues["defaultBranch"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DefaultBranchStyles {
    defaultBranchSelect?: SelectPresentationProps;
}

export const defaultBranchStyles: DefaultBranchStyles = {};

const styles = defaultBranchStyles;

const BranchSelect = Select as React.ComponentType<SelectProps & { children?: React.ReactNode }>;

const DefaultBranch: React.FC<Props> = ({ fields, defaultBranch, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [defaultBranchErrorMessage, setDefaultBranchErrorMessage] = React.useState("");
    const warehouses = useContactUsBranches();

    React.useEffect(() => {
        contactFormContext.validators.defaultBranch = validateDefaultBranch;
        if (typeof defaultBranch !== "undefined") {
            validateDefaultBranch();
        }
    }, [defaultBranch]);

    React.useEffect(() => {
        contactFormContext.validators.defaultBranch = validateDefaultBranch;
    }, [isRequired]);

    const validateDefaultBranch = () => {
        const errorMessage = isRequired && !defaultBranch ? (siteMessage("Field_Required", label) as string) : "";
        setDefaultBranchErrorMessage(errorMessage);
        return !errorMessage;
    };

    const defaultBranchChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "defaultBranch", value: event.target.value });
    };

    return (
        <BranchSelect
            {...(styles.defaultBranchSelect as SelectProps)}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={defaultBranch || ""}
            error={defaultBranchErrorMessage}
            onChange={defaultBranchChangeHandler}
        >
            <option value="">{hintText || translate("Select a branch")}</option>
            {warehouses.map(warehouse => (
                <option key={warehouse.id} value={warehouse.name}>
                    {warehouseOptionLabel(warehouse)}
                </option>
            ))}
        </BranchSelect>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DefaultBranch),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Default Branch",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select a branch",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
